/**
 * 「로그인 정보 저장」이 실제로 저장하는 것 — 원문이 아니라 <b>봉인된 바이트</b>.
 *
 * front 의 `lib/auth/passwordVault.ts` 를 옮겨 왔다. 다른 점은 하나 — 이메일도 함께 봉인한다.
 * 포털은 아이디가 이메일이라 그것까지 매번 치는 것이 실제로 번거롭고, 이메일만 평문으로 두면
 * 「어느 업체 담당자가 이 기기를 쓰는가」가 저장소에 그대로 남는다.
 *
 * <h2>왜 봉인하나</h2>
 * v1 은 `localStorage` 에 평문을 넣었다. 그러면 개발자도구를 열거나 프로필 폴더의 Local Storage
 * 파일을 통째로 복사해 가면 그대로 읽힌다.
 *
 * 여기서는 AES-GCM 으로 봉인해 base64 로만 남긴다. 열쇠는 <b>번들에 없다</b> —
 * {@code extractable: false} 로 만든 CryptoKey 를 IndexedDB 에 두므로, 브라우저는 그 키로
 * 암·복호화만 해 줄 뿐 키 바이트 자체를 내주지 않는다. 저장값을 복사해 가도, 디스크의 브라우저
 * 파일을 통째로 가져가도 원문은 복원되지 않는다.
 *
 * <h2>막지 못하는 것</h2>
 * 이 페이지에서 실행되는 스크립트(XSS)는 아래 {@link recallCredentials} 를 그대로 부를 수 있다.
 * 봉인은 <b>「저장소를 들여다본 사람」에 대한 방어이지 XSS 에 대한 방어가 아니다.</b>
 *
 * <p>그리고 이 앱은 <b>협력사 담당자의 개인 기기</b>에서 돌 수 있다 — 사내 PC 를 전제하는 front
 * 와 다르다. 그래서 기본이 꺼짐이고, 켜는 것은 사람이 고른다.
 */
const STORAGE_KEY = 'bomi.vendor.credentials';

const DB_NAME = 'bomi-vendor-auth';
const DB_VERSION = 1;
const KEY_STORE = 'keys';
const KEY_ID = 'login-credentials';

/** AES-GCM 권장 nonce 길이(12바이트). 봉인값 앞에 그대로 붙여 둔다. */
const IV_BYTES = 12;

/** 열쇠는 한 번만 만든다. 같은 화면에서 두 번 부르면 두 개가 생겨 먼저 봉인한 값이 안 열린다. */
let keyPromise: Promise<CryptoKey> | null = null;

/** 이메일·비밀번호만이다. 문이 하나가 되어(2026-09-29) 「어느 칸이었나」 는 더 적지 않는다 — 서버가 계정을 보고 가른다. */
export type SavedCredentials = { email: string; password: string };

/**
 * 저장한다.
 *
 * <p>봉인에 실패하면 <b>아무것도 남기지 않는다</b> — 평문으로 물러서느니 기억하지 않는 편이 낫다.
 * (사생활 보호 모드처럼 IndexedDB 가 막힌 환경이 여기로 온다.)
 */
export async function rememberCredentials(credentials: SavedCredentials): Promise<void> {
  try {
    const key = await getKey();
    const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
    const sealed = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      new TextEncoder().encode(JSON.stringify(credentials)),
    );

    const packed = new Uint8Array(IV_BYTES + sealed.byteLength);
    packed.set(iv, 0);
    packed.set(new Uint8Array(sealed), IV_BYTES);
    window.localStorage.setItem(STORAGE_KEY, toBase64(packed));
  }
  catch {
    forgetCredentials();
  }
}

/**
 * 되살린다.
 *
 * <p>열리지 않으면(열쇠가 지워졌거나 값이 손상됐다) 남은 값은 쓸모가 없으므로 버리고 null 을
 * 돌려준다 — 사용자는 한 번 더 입력하면 되고, 다음부터 다시 저장된다.
 */
export async function recallCredentials(): Promise<SavedCredentials | null> {
  const stored = readStored();
  if (stored === null) return null;

  try {
    const packed = fromBase64(stored);
    const key = await getKey();
    const opened = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: packed.subarray(0, IV_BYTES) },
      key,
      packed.subarray(IV_BYTES),
    );
    const parsed: unknown = JSON.parse(new TextDecoder().decode(opened));
    if (
      typeof parsed !== 'object' || parsed === null
      || typeof (parsed as SavedCredentials).email !== 'string'
      || typeof (parsed as SavedCredentials).password !== 'string'
    ) {
      forgetCredentials();
      return null;
    }
    // 칸이 둘이던 때 저장된 값에는 `gate` 가 함께 있다. 읽지 않고 버린다 — 문이 하나다.
    const { email, password } = parsed as SavedCredentials;
    return { email, password };
  }
  catch {
    forgetCredentials();
    return null;
  }
}

/** 잊는다. 체크 해제·로그인 실패처럼 즉시 지워야 하는 자리에서 쓰므로 비동기가 아니다. */
export function forgetCredentials(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  }
  catch {
    // 저장소 접근이 막힌 환경. 애초에 저장된 것도 없다.
  }
}

/** 저장된 것이 있는가. 로그인 화면이 체크박스의 초기 상태를 이것으로 정한다. */
export function hasSavedCredentials(): boolean {
  return readStored() !== null;
}

function readStored(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  }
  catch {
    return null;
  }
}

// ── 열쇠 ────────────────────────────────────────────────────────────────────

function getKey(): Promise<CryptoKey> {
  keyPromise ??= loadOrCreateKey().catch((error: unknown) => {
    // 실패한 약속을 캐시에 남기면 이후 시도가 전부 같은 실패를 재생한다.
    keyPromise = null;
    throw error;
  });
  return keyPromise;
}

async function loadOrCreateKey(): Promise<CryptoKey> {
  const db = await openDatabase();
  try {
    const existing = await requestAsPromise<CryptoKey | undefined>(
      db.transaction(KEY_STORE, 'readonly').objectStore(KEY_STORE).get(KEY_ID),
    );
    if (existing !== undefined) return existing;

    // extractable=false 가 이 설계의 전부다. true 면 저장값과 열쇠를 함께 긁어가 원문을 복원할 수 있다.
    const created = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, [
      'encrypt',
      'decrypt',
    ]);
    await requestAsPromise(
      db.transaction(KEY_STORE, 'readwrite').objectStore(KEY_STORE).put(created, KEY_ID),
    );
    return created;
  }
  finally {
    db.close();
  }
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(KEY_STORE)) {
        request.result.createObjectStore(KEY_STORE);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    // 다른 탭이 옛 버전을 잡고 있으면 열리지 않는다. 걸린 채로 두면 로그인 화면이 멈추므로 실패로 본다.
    request.onblocked = () => reject(new Error('IndexedDB blocked'));
  });
}

function requestAsPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// ── base64 ──────────────────────────────────────────────────────────────────

function toBase64(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

// 반환형에 ArrayBuffer 를 못박는다 — 기본값(ArrayBufferLike)은 SharedArrayBuffer 를 포함해
// WebCrypto 의 BufferSource 로 넘어가지 않는다.
function fromBase64(value: string): Uint8Array<ArrayBuffer> {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}
